import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  Select,
  Input,
  FormHelperText,
  Badge,
  useToast,
} from "@chakra-ui/react";
import api from "utils/api";

const ACCESSORY_LABELS = {
  blockingRelay: "Blokaj Rölesi",
};

const ACCESSORY_TYPE_RULES = [
  { match: ["blokaj", "röle", "role", "blockingrelay"], type: "blockingRelay" },
];

function parseAccessories(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function resolveAccessoryTypeByName(name) {
  if (!name) return null;
  const lower = String(name).toLowerCase();
  const rule = ACCESSORY_TYPE_RULES.find((r) => r.match.some((m) => lower.includes(m)));
  return rule?.type || null;
}

export default function AksesuarIslemleriModal({ isOpen, onClose, aracData, onSuccess }) {
  const toast = useToast();
  const [aksesuarOptions, setAksesuarOptions] = useState([]);
  const [selectedAksesuarId, setSelectedAksesuarId] = useState("");
  const [selectedDisableType, setSelectedDisableType] = useState("");
  const [enableAdet, setEnableAdet] = useState("1");
  const [disableAdet, setDisableAdet] = useState("1");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEnableAdet("1");
    setDisableAdet("1");
    const load = async () => {
      try {
        const res = await api.get("/api/aksesuarlar/");
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || "Aksesuarlar yüklenemedi.");
        setAksesuarOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        setAksesuarOptions([]);
        toast({
          title: "Aksesuarlar yüklenemedi",
          description: err.message,
          status: "error",
        });
      }
    };
    load();
  }, [isOpen, toast]);

  const currentAccessories = useMemo(
    () => parseAccessories(aracData?.accessories),
    [aracData?.accessories]
  );

  const enabledAccessories = useMemo(
    () => currentAccessories.filter((a) => a?.type && a?.enabled === true),
    [currentAccessories]
  );
  const enabledTypes = useMemo(
    () => new Set(enabledAccessories.map((a) => a.type)),
    [enabledAccessories]
  );

  const selectedAccessory = useMemo(
    () => aksesuarOptions.find((a) => String(a.id) === String(selectedAksesuarId)) || null,
    [aksesuarOptions, selectedAksesuarId]
  );
  const selectedAccessoryType = useMemo(
    () => resolveAccessoryTypeByName(selectedAccessory?.name),
    [selectedAccessory]
  );
  const isSelectedTypeAlreadyEnabled = !!(
    selectedAccessoryType && enabledTypes.has(selectedAccessoryType)
  );

  useEffect(() => {
    if (!isOpen) return;
    if (enabledAccessories.length > 0) {
      setSelectedDisableType((prev) => prev || enabledAccessories[0].type);
    } else {
      setSelectedDisableType("");
    }
  }, [isOpen, enabledAccessories]);

  const disableCandidate = useMemo(() => {
    if (!selectedDisableType) return null;
    return (
      aksesuarOptions.find((opt) =>
        selectedDisableType === "blockingRelay"
          ? opt.name?.toLowerCase().includes("blokaj")
          : false
      ) || null
    );
  }, [selectedDisableType, aksesuarOptions]);

  const handleEnable = async () => {
    if (!aracData?.id || !selectedAksesuarId || isSelectedTypeAlreadyEnabled) return;
    const parsedAdet = Number(enableAdet);
    if (!Number.isInteger(parsedAdet) || parsedAdet < 1) {
      toast({ title: "Geçerli bir adet giriniz", status: "warning" });
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post(`/api/devices/${aracData.id}/aksesuar`, {
        action: "enable",
        aksesuar_id: Number(selectedAksesuarId),
        aksesuar_adet: parsedAdet,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Aksesuar eklenemedi.");
      toast({ title: "Aksesuar eklendi", status: "success" });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast({ title: "İşlem başarısız", description: err.message, status: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (!aracData?.id || !disableCandidate?.id) return;
    const parsedAdet = Number(disableAdet);
    if (!Number.isInteger(parsedAdet) || parsedAdet < 1) {
      toast({ title: "Geçerli bir adet giriniz", status: "warning" });
      return;
    }
    setProcessing(true);
    try {
      const res = await api.post(`/api/devices/${aracData.id}/aksesuar`, {
        action: "disable",
        aksesuar_id: Number(disableCandidate.id),
        aksesuar_adet: parsedAdet,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Aksesuar pasif edilemedi.");
      toast({ title: "Aksesuar pasif edildi", status: "success" });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      toast({ title: "İşlem başarısız", description: err.message, status: "error" });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader>Aksesuar İşlemleri</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Box bg="gray.50" borderRadius="md" p={3}>
              <Text fontSize="sm" color="gray.600">
                Araç: <Text as="span" fontWeight="semibold">{aracData?.plaka || "-"}</Text>
              </Text>
              <Text fontSize="xs" color="gray.500">IMEI: {aracData?.imei || "-"}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={2}>Bağlı Aksesuarlar</Text>
              {currentAccessories.length ? (
                <VStack align="stretch" spacing={2}>
                  {currentAccessories.map((item, idx) => (
                    <HStack key={`${item?.type || "unknown"}-${idx}`} justify="space-between">
                      <Text fontSize="sm">{ACCESSORY_LABELS[item?.type] || item?.type || "Bilinmiyor"}</Text>
                      <Badge colorScheme={item?.enabled ? "green" : "gray"}>
                        {item?.enabled ? "Aktif" : "Pasif"}
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">Bu araçta bağlı aksesuar yok.</Text>
              )}
            </Box>

            <FormControl>
              <FormLabel fontSize="sm">Aksesuar Ekle</FormLabel>
              <Select
                placeholder="Aksesuar seçiniz"
                value={selectedAksesuarId}
                onChange={(e) => setSelectedAksesuarId(e.target.value)}
              >
                {aksesuarOptions.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
              </Select>
              <FormHelperText>
                {isSelectedTypeAlreadyEnabled
                  ? "Bu aksesuar tipi zaten aktif. Aynı tip tekrar eklenemez."
                  : "Aynı tip aksesuar tekrar eklenemez, farklı tipler eklenebilir."}
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Eklenecek Adet</FormLabel>
              <Input
                type="number"
                min={1}
                step={1}
                value={enableAdet}
                onChange={(e) => setEnableAdet(e.target.value)}
              />
            </FormControl>

            {enabledAccessories.length > 0 && (
              <>
                <FormControl>
                  <FormLabel fontSize="sm">Pasif Edilecek Bağlı Aksesuar</FormLabel>
                  <Select
                    placeholder="Bağlı aksesuar seçiniz"
                    value={selectedDisableType}
                    onChange={(e) => setSelectedDisableType(e.target.value)}
                  >
                    {enabledAccessories.map((acc, idx) => (
                      <option key={`${acc.type}-${idx}`} value={acc.type}>
                        {ACCESSORY_LABELS[acc.type] || acc.type}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Pasifleştirilecek Adet</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={disableAdet}
                    onChange={(e) => setDisableAdet(e.target.value)}
                  />
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2}>
            {enabledAccessories.length > 0 && (
              <Button onClick={handleDisable} colorScheme="orange" isDisabled={!selectedDisableType || processing} isLoading={processing}>
                Pasifleştir
              </Button>
            )}
            <Button onClick={handleEnable} colorScheme="blue" isDisabled={!selectedAksesuarId || isSelectedTypeAlreadyEnabled || processing} isLoading={processing}>
              Ekle
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
